import { useQuery } from '@tanstack/react-query';

import { AppID, Filter, OptionsFilter } from '@playq/octopus-common';
import { App, AppsFilterField } from '@playq/octopus2-apps';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { createRequestQueue, IUseQueryOptionsExtended } from '/api/service-hooks';
import { services2 } from '/api/services2';

import { appQueryTags } from './constants';

const delay = 50;

const makeFilterBy = (appsID: (AppID | undefined)[]): Record<string, Filter> => {
  const filterBy: Record<string, Filter> = {};
  const filteredAppsID = appsID.filter(Boolean) as AppID[];

  if (filteredAppsID.length > 0) {
    const options = filteredAppsID.map((appID) => appID.serialize());
    const filter = new OptionsFilter({ options });
    filterBy[AppsFilterField.ID] = filter;
  }

  return filterBy;
};

const queue = createRequestQueue<AppID | undefined, App>(
  (appsID) => {
    const filterBy = makeFilterBy(appsID);
    return new Promise((resolve, reject) => {
      services2.appsService
        .queryApps(new OffsetLimit({ offset: 0, limit: appsID.length }), [], filterBy)
        .then((data) => data.bifold((res) => resolve(res.entities), reject));
    });
  },
  {
    predicate: (appId, app) => (app ? appId?.serialize() === app.id.serialize() : false),
    timeout: delay,
  }
);

export const useAppQuery = (id?: AppID, options?: IUseQueryOptionsExtended<App | null, GenericFailure | Error>) => {
  return useQuery<App | null, GenericFailure | Error>({
    queryKey: [...appQueryTags, id],
    queryFn: () => queue.push(id),
    enabled: id !== undefined && (options?.enabled ?? true),
    ...options,
  });
};
